import React from 'react';
import { PopperProps, Popper } from '@material-ui/core';

// Each wide-layout column keeps the same 700px width classes.root's own width/maxWidth
// already use for the single-column drawer (see tabsContentAreaWide's own comment) - 3 of
// them plus the 2 columnGaps between them plus classes.root's own 15px left/right padding.
export const WIDE_LAYOUT_DRAWER_WIDTH = (3 * 700) + (2 * 24) + (2 * 15);

// Explicit request: when a docked Quick View eats into the available width, shrink the middle
// column (Activity/Further Details) down to this floor before giving up on 3-column layout
// entirely - Details and Follow-Up/Matter stay exactly 700px (their own 2-up field Grids are
// the ones that broke at narrower widths per tabsContentAreaWide's own earlier bug report;
// Activity's own cards are flex-based, not fixed-width-assuming, so they reflow safely).
export const ACTIVITY_COLUMN_MIN_WIDTH = 480;
export const WIDE_LAYOUT_DRAWER_MIN_WIDTH = 700 + ACTIVITY_COLUMN_MIN_WIDTH + 700 + (2 * 24) + (2 * 15);

// Explicit request: a new in-between layout once 3 columns no longer fit - Details/Activity/
// Further Details share one column (behind a small tab switcher, see tabsContentAreaMedium's
// own header), Follow-Up/Matter keep their existing independent column unchanged (same 'third'
// gridArea/content wide layout already uses). Same per-column width conventions as
// WIDE_LAYOUT_DRAWER_WIDTH/_MIN_WIDTH above, just 2 columns instead of 3 - the shared left
// column gets the same shrink-to-ACTIVITY_COLUMN_MIN_WIDTH floor the wide layout's own middle
// column already has (same tradeoff that fix accepted: Details/Further Details read a little
// tighter at the floor width, in exchange for never overflowing the drawer's own bounds).
export const MEDIUM_LAYOUT_DRAWER_WIDTH = (2 * 700) + 24 + (2 * 15);
export const MEDIUM_LAYOUT_DRAWER_MIN_WIDTH = ACTIVITY_COLUMN_MIN_WIDTH + 700 + 24 + (2 * 15);

// The Follow Up/Activity tab's own fields - hoisted to module scope (was a local const inside
// the main component) so ActivityTabSection can read the exact same list its own Save follow-up
// button needs (see that button's own disabled logic) without prop-drilling the function down,
// and resetFollowUpFields/detectUpdatedTab inside the main component still call it exactly as
// before - a pure, dependency-free function, safe to hoist.
export const getActivitiesTabFields = () => [
    'followUpAction',
    'staff',
    'isSendConfirmation',
    'officeLocation',
    'timezone',
    'startTime',
    'endTime',
    'isAllDay',
    'meetingNotes',
    'feeApplicable',
    'emailTemplate',
    'followUpDate',
    'followUpNotes',
    'outcome',
    'followUpDueReminder',
    'outcomeNotes',
    'reason',
    'referredToFirmName',
    'referredToFirmSelector',
    'notProceedingOutcomeNotes'
];

// Every Accordion on the Details tab that has its own expand/collapse state (one flag per
// panel, each wired to onExpandPanel(key) - see getContactDetails/getEnquiryDetails/
// getAssignment/etc.) - the Expand All button next to the Details heading just sets all of
// these at once rather than needing its own separate tracking.
export type DetailsTabPanelKey = 'isContactDetailsPanelExpand' | 'isEnquiryDetailsPanelExpand'
    | 'isDetailedEnquiryNotesPanelExpand' | 'isDigitalTrackingPanelExpand' | 'isAssignPanelExpand'
    | 'isOtherContactDetailsPanelExpand' | 'isOtherDetailsPanelExpand' | 'isOther2DetailsPanelExpand'
    | 'isClient2PostalAddressPanelExpand' | 'isPostalAddressPanelExpand'
    | 'isOtherSidePostalAddressPanelExpand' | 'isOtherSidePanelExpand'
    | 'isOrganisationPanelExpand' | 'isEnquiryValuePanelExpand';

export const DETAILS_TAB_PANEL_KEYS: DetailsTabPanelKey[] = [
    'isContactDetailsPanelExpand',
    'isEnquiryDetailsPanelExpand',
    'isDetailedEnquiryNotesPanelExpand',
    'isDigitalTrackingPanelExpand',
    'isAssignPanelExpand',
    'isOtherContactDetailsPanelExpand',
    'isOtherDetailsPanelExpand',
    'isOther2DetailsPanelExpand',
    'isClient2PostalAddressPanelExpand',
    'isPostalAddressPanelExpand',
    'isOtherSidePostalAddressPanelExpand',
    'isOtherSidePanelExpand',
    'isOrganisationPanelExpand',
    'isEnquiryValuePanelExpand',
];

// Explicit performance report: typing in any Details-tab field re-rendered this whole section
// (including <HistoryActivity>, a heavy timeline) on every keystroke, because activityTab() used
// to be a plain closure called inline (`{activityTab(form, pristine, values)}`) inside the Form's
// own render-prop - a plain function call is invisible to React's reconciler, so there was no
// component boundary for React.memo to bail out at, no matter what. Hoisting it to a real,
// module-level React.memo component (with the same free-variable names destructured straight
// back out of props below, so the 1300-line body itself needed zero changes) gives it one.
// Neither the outer `values` nor `pristine` parameter was ever read directly in the body (every
// place that needs live values already re-subscribes via its own local
// <FormSpy subscription={{ values: true }}> - see each one's own
// "tslint:disable-next-line: no-shadowed-variable" comment) - confirmed by reading the full
// function, not assumed - so dropping the top-level Form's own values/errors subscription was
// never required; the fix is entirely about this component boundary.
// Pure/self-contained (no closure dependencies) - moved to module scope alongside
// ActivityTabSection, which is the only place that used it, since it's no longer reachable as a
// closure from inside EnquiryFormDrawer once ActivityTabSection was hoisted out.
export const CustomPopper = function (popperProps: PopperProps) {
    return <Popper {...popperProps} placement="top" />;
};

export enum EnquiryFormTab {
    details = 0,
    activity,
    matterDetails,
    customFields
}

export const getDetailsTabFields = () => [
    // contact details
    'title',
    'firstName',
    'individualSelector',
    'lastName',
    'initialEnquiryDate',
    'email',
    'phone',
    'mobile',
    'source',
    'office',
    'referrer',
    'sourceNotes',
    'internalNotes',
    'eventAndCampaignSelector',
    'eventsAndCampaignNotes',

    // organisation

    'organisationSelector',
    'organisation',
    'positionAtOrganisation',

    // enquiryDetails
    'natureOfEnquiry',
    'areaOfLaw',

    // enquiryValuePanels
    'estimatedFees',
    'isEstimatedFeesIncludesTax',
    'estimatedDisbursements',
    'isEstimatedDisbursementsIncludesTax',
    'enquiryQuality',
    'likelihoodToProceed',
    'enquiryNotes',


    // otherDetailsPanel
    'clientTitle',
    'clientFirstName',
    'clientIndividualSelector',
    'clientLastName',
    'clientRelationshipToEnquirer',
    'clientEmail',
    'clientPhone',
    'clientMobile',
    'dOB',
    'gender',
    'isMarketingConsent',
    'isTermsAndConditionsApproved',
    'isPrivacyStatementProvided',
    'isConflictCheckPerformed',
    'isNoConflict',
    'isIdentityCheckPerformed',

    // clientAddressPanel
    'addressName',
    'floor',
    'number',
    'street',
    'street2',
    'suburb',
    'state',
    'postcode',
    'country',

    // clientPostalAddressPanel
    'pOBoxType',
    'pOBox',
    'postalSuburb',
    'postalState',
    'postalPostCode',
    'postalCountry',


    // otherDetails2Panel
    'secondClientTitle',
    'secondClientFirstName',
    'secondClientIndividualSelector',
    'secondClientLastName',
    'secondClientRelationshipToEnquirer',
    'secondClientEmail',
    'secondClientPhone',
    'secondClientMobile',
    'secondClientDOB',
    'secondClientGender',
    'secondClientIsMarketingConsent',
    'secondClientIsTermsAndConditionsApproved',
    'secondClientIsPrivacyStatementProvided',
    'secondClientIsConflictCheckPerformed',
    'secondClientIsNoConflict',
    'secondClientIsIdentityCheckPerformed',

    // client2AddressPanel
    'secondClientAddressName',
    'secondClientFloor',
    'secondClientNumber',
    'secondClientStreet1',
    'secondClientStreet2',
    'secondClientSuburb',
    'secondClientState',
    'secondClientPostCode',
    'secondClientCountry',

    // client2PostalAddressPanel
    'secondClientPOBoxType',
    'secondClientPOBox',
    'secondClientPostalSuburb',
    'secondClientPostalState',
    'secondClientPostalPostCode',
    'secondClientPostalCountry',

    // riskAssessedPanel
    'isRiskAssessed',
    'riskLevelSelector',
    'riskNotes',

    // otherSidePanel
    'otherPartyTitle',
    'otherPartyFirstName',
    'otherPartyIndividualSelector',
    'otherPartyLastName',
    'otherPartyEmail',
    'otherPartyPhone',
    'otherPartyMobile',
    'otherPartyDOB',
    'otherPartyOrganisation',
    'otherPartyNotes',

    // detailedEnquiryNotesPanel
    'responseText',

    // custom fields
    'customField'
];
